import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Skeleton } from '@hungryroot/pho';
import { PDPModal } from '@hungryroot/rdp';
import { EDIT_DELIVERY_GROCERIES_ROUTE } from '@hungryroot/routes';
import { buttonClickedEvent, productInteraction } from '@hungryroot/segment';
import { Button, Heading, Paragraph } from '@hungryroot/ui-kit';

import { useDietaryPreferencesUrl } from '../../../hooks/useDietaryPreferencesUrl';
import { useNextNavigation } from '../../../hooks/useNextNavigation';
import { useReadyToEatProducts } from '../../../hooks/useReadyToEatProducts';
import { ProductCard } from './components/ProductCard';
import { SkeletonCard } from './components/SkeletonCard';
import styles from './MorePreparedMealsDeflection.module.css';

const SEE_MORE_URL = `${EDIT_DELIVERY_GROCERIES_ROUTE}?is_ready_to_eat=1`;
const SKELETON_COUNT = 9;

export function MorePreparedMealsDeflection() {
  const { data, isLoading } = useReadyToEatProducts();
  const [pdpProductId, setPdpProductId] = useState<number | null>(null);
  const dietaryPreferencesUrl = useDietaryPreferencesUrl({
    foodProfileSurface: 'dietary_preferences',
  });
  const { navigateFromDeflectionContinue } = useNextNavigation();
  const history = useHistory();

  const products = data?.products ?? [];
  const count = data?.count;
  const hasNothingToShow = !isLoading && products.length === 0;

  // If there are no prepared meals to surface there's nothing for the customer
  // to react to, so advance past the deflection automatically. Use `replace`
  // so hitting browser back doesn't bounce the customer back onto this empty
  // page.
  useEffect(() => {
    if (hasNothingToShow) {
      navigateFromDeflectionContinue({ replace: true });
    }
  }, [hasNothingToShow, navigateFromDeflectionContinue]);

  if (hasNothingToShow) {
    return null;
  }

  return (
    <div className={styles.container}>
      {isLoading ? (
        <>
          <Skeleton className={styles['skeleton-title']} />
          <Skeleton className={styles['skeleton-subcopy']} />
          <Skeleton className={styles['skeleton-subcopy']} />
          <div className={styles.grid}>
            {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </>
      ) : (
        <>
          <Heading variant="Hero" className={styles.title}>
            We Hear You — Let's Find Your Perfect Meals
          </Heading>
          <Paragraph className={styles.intro}>
            Based on what you shared with us, it looks like you want more
            heat-and-eat and ready-to-eat options.
          </Paragraph>
          <Paragraph className={styles.subcopy}>
            Hungryroot has{' '}
            <span className={styles['subcopy-highlight']}>
              {count ?? '75+'} prepared meals
            </span>{' '}
            that meet{' '}
            <a
              href={dietaryPreferencesUrl}
              className={styles.link}
              onClick={() => {
                buttonClickedEvent({
                  button_name: 'your dietary needs',
                  order_id: null,
                  page_position: 'cancel_deflection_more_prepared_meals',
                });
              }}
            >
              your dietary needs
            </a>{' '}
            and this is our fastest growing category! Our chefs are expanding
            our selection every month. We offer ready-to-eat meals as well as
            heat-and-eat. Take a look at what's available this week — you might
            be surprised.
          </Paragraph>

          <Heading variant="H4" className={styles.heading}>
            Prepared meals
          </Heading>

          <div className={styles.grid}>
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onClick={() => {
                  productInteraction({
                    productId: product.id,
                    productName: product.name,
                    actionType: 'clicked',
                    location: 'card',
                  });
                  setPdpProductId(product.id);
                }}
              />
            ))}
          </div>

          <div className={styles['cta-container']}>
            <Button
              onClick={() => {
                buttonClickedEvent({
                  button_name: 'See all prepared meals',
                  order_id: null,
                  page_position: 'cancel_deflection_more_prepared_meals',
                });
                history.push(SEE_MORE_URL);
              }}
              style="default"
              size="large"
              prominence="primary"
              textPrimary={`See All ${count} Prepared Meals`}
            />
          </div>
        </>
      )}

      {pdpProductId !== null && (
        <PDPModal
          id={pdpProductId}
          onDismiss={() => setPdpProductId(null)}
          stacked={false}
          footerButton={<></>}
          enableFoodProfileNeverWarnings={true}
        />
      )}
    </div>
  );
}
